import React, { useState } from "react";
import { FolderOpen, SquarePen } from "lucide-react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";

import OverviewModal from "./OverviewModal";
import AddAssetModal from "./AddAssetModal";
import EditAssetModal from "./EditAssetModal";

import {
  selectAssetsError,
  selectAssetsGroupedByClass,
  selectAssetsStatus,
} from "../Logic/assetsSlice";

import { fetchAssets } from "../Logic/assetsThunk";

import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { ASSET_CLASS_LABELS, ASSET_TYPE_LABELS } from "../../../api/assets";

type AssetOverviewView =
  | { mode: "list" }
  | { mode: "add" }
  | { mode: "edit"; assetId: string };    
  
interface AssetsOverviewModalProps {
  onClose: () => void;
}

const AssetsOverviewModal: React.FC<AssetsOverviewModalProps> = ({
  onClose,
}) => {
  const [view, setView] = useState<AssetOverviewView>({mode: "list"});

  const groupedAssets = useAppSelector(selectAssetsGroupedByClass);
  const status = useAppSelector(selectAssetsStatus);

  const isLoading = status === "loading";
  const fetchError = useAppSelector(selectAssetsError);

  const groupedAssetEntries = Object.entries(groupedAssets);
  const hasAssets = groupedAssetEntries.length > 0;

  const dispatch = useAppDispatch();

  const { id: case_id } = useParams<{ id: string }>();

  const handleRetry = () => {
    if (!case_id) {
      return;
    }

    dispatch(fetchAssets(case_id));
  };

  if (view.mode === "add") {
    return (
      <AddAssetModal
        onClose={onClose}
        onBack={() => setView({ mode: "list" })}
      />
    );
  }

  if (view.mode === "edit") {
    return (
      <EditAssetModal
        assetId={view.assetId}
        onClose={onClose}
        onBack={() => setView({ mode: "list" })}
      />
    );
  }

  return (
    <OverviewModal
      title="Assets"
      addButtonLabel="Add New Asset"
      onAction={() =>setView({ mode: "add" })}
      onClose={onClose}
    >
      {isLoading ? (
        <CustomLoadingSpinner
          title="Loading Assets..."
          subtitle="Fetching assets associated with this case."
          minHeight="min-h-[160px]"
        />
      ) : fetchError ? (
        <ErrorDisplay
          error="Unable to load assets. Please try again."
          onRetry={handleRetry}
        />
      ) : !hasAssets ? (
        <div className="flex min-h-[300px] flex-col items-center justify-center gap-3">
          <FolderOpen
            size={40}
            className="text-[#A3A3A3]"
          />

          <div className="text-sm font-medium text-[#0A0A0A]">
            No assets added
          </div>

          <div className="text-xs text-[#737373]">
            Add your first asset to continue.
          </div>
        </div>
      ) : (
        <div >
          {groupedAssetEntries.map(
            (
              [assetClass, assets],
              sectionIndex,
            ) => (
              <div
                key={assetClass}
                className={
                  sectionIndex > 0
                    ? "pt-2"
                    : ""
                }
              >
                <div className="pb-1 text-[12px] font-medium text-[#737373]">
                  {
                    ASSET_CLASS_LABELS[
                      assetClass as keyof typeof ASSET_CLASS_LABELS
                    ]
                  }
                </div>

                <div className="flex flex-col">
                  {assets.map(asset => (
                    <div
                      key={
                        asset.asset_id
                      }
                      className="flex items-center justify-between py-2"
                    >
                      <div className="flex-1">
                        <div className="max-w-[350px] truncate text-[14px] font-normal leading-5 text-[#0A0A0A]">
                          {
                            asset.asset_name
                          }
                        </div>

                        <div className="text-[12px] font-normal leading-4 text-[#737373]">
                          {
                            asset.ext_asset_id
                          }
                          {" • "}
                          {ASSET_TYPE_LABELS[asset.asset_type]}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                         setView({
                            mode: "edit",
                            assetId: asset.asset_id,
                          })
                        }
                        className="flex h-9 w-9 items-center justify-center rounded-[8px] opacity-50 transition-colors"
                      >
                        <SquarePen
                          size={16}
                          className="text-[#0A0A0A]"
                        />
                      </button>
                    </div>
                  ))}
                </div>

                {sectionIndex !==
                  groupedAssetEntries.length -
                    1 && (
                  <div className="mt-2 border-b border-[#E5E5E5]" />
                )}
              </div>
            ),
          )}
        </div>
      )}
    </OverviewModal>
  );
};

export default AssetsOverviewModal;